import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import GetCookie from "../../../../utils/GetCookie";
import authNavigate from "../../../../globalComponents/AuthNav";
import HumanReviewerAllocation from "./HumanReviewerAllocation";
import AiModelConfiguration from "./AiModelConfiguration";

// Default AI agent model: Sonnet 4.6 when it is offered, otherwise the first
// available model (the list order from the backend is not guaranteed).
const PREFERRED_DEFAULT_MODEL = "claude-sonnet-4-6";
// Mirrors AiModelConfiguration: only the Claude models are wired up, so the
// default can never fall through to a model the user is not allowed to pick.
// Keep in sync with AiModelConfiguration's list of the same name.
const DISABLED_MODEL_KEYS = ["claude-opus-5"];
const isModelSelectable = (modelKey) => {
  const key = String(modelKey || "").toLowerCase();
  if (DISABLED_MODEL_KEYS.includes(key)) return false;
  return key.startsWith("claude");
};
const pickDefaultModel = (models) => {
  const keys = (models || []).map((m) => m.model_key).filter(isModelSelectable);
  if (keys.includes(PREFERRED_DEFAULT_MODEL)) return PREFERRED_DEFAULT_MODEL;
  return keys[0] || PREFERRED_DEFAULT_MODEL;
};

const ScreeningFrameworkSelectionForm = forwardRef(({ projectId, teamId, stageData, abstractsCount, fullTextCount, availableModels, onDataChange, onRefreshCounts }, ref) => {
  const navigate = useNavigate();
  const API_BASE_URL = "/pceapi/slr/form";
  const headers = useMemo(() => ({ Authorization: GetCookie("access_token") }), []);

  // ====== Framework selection ======
  const [frameworkGroup, setFrameworkGroup] = useState(null); // null | single | dual
  const [frameworkType, setFrameworkType] = useState(null); // null | human_only | ai_only | human_ai | human_human
  const hasFrameworkSelected = Boolean(frameworkGroup && frameworkType);
  const [aiAssistantAbstract, setAiAssistantAbstract] = useState(false);
  const [aiAssistantFulltext, setAiAssistantFulltext] = useState(false);

  // Edit mode - framework can be changed until screening has actually started
  const isEditMode = Boolean(stageData?.screening_started);

  // Abstract stage is locked once full-text screening has started — its AI
  // agents can no longer be added, removed or changed.
  const isAbstractLocked = Boolean(stageData?.abstract_locked);

  // ====== Allocation tabs ======
  const [allocationType, setAllocationType] = useState("abstract"); // abstract | fulltext
  const phaseKey = allocationType === "abstract" ? "phase1" : "phase2";

  // ====== Human-Human Set tabs ======
  const [selectedSet, setSelectedSet] = useState(1); // 1 | 2

  // ====== Human-AI sub tabs (within each phase) ======
  const [humanAiTab, setHumanAiTab] = useState("human"); // human | ai

  // ====== Team Data (from API) ======
  const [team, setTeam] = useState([]);
  const [teamTotal, setTeamTotal] = useState(0);
  const [isLoadingTeam, setIsLoadingTeam] = useState(false);

  // ====== Totals (abstracts count & full text count from general_settings) ======
  const [totals, setTotals] = useState({ abstract: 0, fulltext: 0 });

  // Update totals when counts change
  useEffect(() => {
    setTotals({
      abstract: abstractsCount || 0,
      fulltext: fullTextCount || 0
    });
  }, [abstractsCount, fullTextCount]);


  const [isRefreshingCounts, setIsRefreshingCounts] = useState(false);

  const handleRefreshCounts = async () => {
    if (typeof onRefreshCounts !== "function") return;

    setIsRefreshingCounts(true);
    try {
      await onRefreshCounts();
    } catch (err) {
      console.error("Error refreshing counts:", err);
    } finally {
      setIsRefreshingCounts(false);
    }
  };


  // Get current total based on allocationType
  const currentTotal = allocationType === "abstract" ? totals.abstract : totals.fulltext;

  // allocations per phase + set
  const [humanAllocations, setHumanAllocations] = useState({
    phase1: { set1: [], set2: [] },
    phase2: { set1: [], set2: [] },
  });

  // AI config per phase
  const [aiConfig, setAiConfig] = useState({
    phase1: [],
    phase2: [],
  });

  // ====== Search + pagination ======
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const isHumanHuman = hasFrameworkSelected && frameworkType === "human_human";
  const isHumanAi = hasFrameworkSelected && frameworkType === "human_ai";
  const isHumanOnly = hasFrameworkSelected && frameworkType === "human_only";
  const isAiOnly = hasFrameworkSelected && frameworkType === "ai_only";
  const showAiAssistantToggle = hasFrameworkSelected && !isAiOnly;

  // active bucket selector for human allocations
  const activeSetKey =
    frameworkType === "human_human"
      ? selectedSet === 1
        ? "set1"
        : "set2"
      : "set1";

  const otherSetKey = activeSetKey === "set1" ? "set2" : "set1";

  const reviewers = humanAllocations[phaseKey][activeSetKey] || [];
  const otherSetReviewers = humanAllocations[phaseKey][otherSetKey] || [];
  const excludeUserIds = useMemo(
    () => (isHumanHuman ? otherSetReviewers.map((r) => r.user_id) : []),
    [isHumanHuman, otherSetReviewers]
  );

  const restoreFromStageData = () => {
    setAllocationType("abstract");
    setSelectedSet(1);
    setHumanAiTab("human");

    // If stageData exists, re-initialize allocations / agents from it, but keep
    // the framework the user just selected.
    if (stageData) {
      initializeFromStageData(stageData, { applyFramework: false });
    } else {
      // No stageData, reset to empty
      setHumanAllocations({
        phase1: { set1: [], set2: [] },
        phase2: { set1: [], set2: [] },
      });
      setAiConfig({
        phase1: [],
        phase2: [],
      });
    }
  };

  // Auto-initialize a default AI agent whenever an AI-capable framework is
  // selected for the first time (no stageData / new project). In edit mode
  // initializeFromStageData already ensures at least 1 agent exists.
  useEffect(() => {
    if (isEditMode) return;
    const needsAi = frameworkType === "ai_only" || frameworkType === "human_ai";
    if (!needsAi) return;

    const defaultModel = pickDefaultModel(availableModels);
    const defaultAgent = { id: 1, model: defaultModel, temperature: 0.2 };

    setAiConfig({
      phase1: { agents: [defaultAgent], default_agent_id: 1 },
      phase2: { agents: [defaultAgent], default_agent_id: 1 },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameworkType]);

  /* ===================== FETCH TEAM MEMBERS ===================== */
  useEffect(() => {
    if (teamId) {
      fetchTeamMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, page, pageSize, search]);

  const fetchTeamMembers = async () => {
    if (!teamId) return;

    setIsLoadingTeam(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/team-members`,
        {
          team_id: teamId,
          page,
          size: pageSize,
          search: search || undefined
        },
        { headers }
      );

      const data = response.data;

      if (data.status === "NotAuthorized") {
        authNavigate("/login", "iam", navigate);
        return;
      }

      if (data.status === "failed") {
        console.error("Failed to fetch team members:", data.message);
        return;
      }

      const members =
        data?.data?.team_members ||
        data?.team_members ||
        data?.data?.data ||
        data?.data ||
        [];

      setTeam(Array.isArray(members) ? members : []);

      const total =
        data?.total ??
        data?.data?.total ??
        0;

      setTeamTotal(total);

    } catch (err) {
      console.error("Error fetching team members:", err);
    } finally {
      setIsLoadingTeam(false);
    }
  };

  useEffect(() => {
    if (stageData) {
      initializeFromStageData(stageData);
    }
  }, [stageData, availableModels]);

  useEffect(() => {
    if (!stageData) return;
    setAiAssistantAbstract(!!stageData.phase1_ai_assist_enabled);
    setAiAssistantFulltext(!!stageData.phase2_ai_assist_enabled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageData]);

  const initializeFromStageData = (data, { applyFramework = true } = {}) => {
    // Set framework — skipped when re-initializing after the user picked a
    // framework, otherwise the saved framework would overwrite their click.
    if (applyFramework && data.framework) {
      const frameworkMap = {
        'SINGLE_HUMAN_ONLY': { group: 'single', type: 'human_only' },
        'SINGLE_AI_ONLY': { group: 'single', type: 'ai_only' },
        'DUAL_HUMAN_AI': { group: 'dual', type: 'human_ai' },
        'DUAL_HUMAN_HUMAN': { group: 'dual', type: 'human_human' }
      };
      const mapped = frameworkMap[data.framework];
      if (mapped) {
        setFrameworkGroup(mapped.group);
        setFrameworkType(mapped.type);
      }
    }

    {
      const set1 = [];
      const set2 = [];

      (data.phase1_human_allocations || []).forEach((alloc, idx) => {
        const reviewer = {
          rowId: `${alloc.reviewer_id}-restored-${idx}-${Date.now()}`,
          user_id: alloc.reviewer_id,
          email: alloc.email,
          start: alloc.allocation_range?.start?.toString() || "",
          end: alloc.allocation_range?.end?.toString() || ""
        };

        if (alloc.reviewer_set === "SET_1") {
          set1.push(reviewer);
        } else if (alloc.reviewer_set === "SET_2") {
          set2.push(reviewer);
        }
      });

      setHumanAllocations((prev) => ({
        ...prev,
        phase1: { set1, set2 }
      }));
    }

    // Set Phase 2 Human Allocations
    {
      const set1 = [];
      const set2 = [];

      (data.phase2_human_allocations || []).forEach((alloc, idx) => {
        const reviewer = {
          rowId: `${alloc.reviewer_id}-restored-p2-${idx}-${Date.now()}`,
          user_id: alloc.reviewer_id,
          email: alloc.email,
          start: alloc.allocation_range?.start?.toString() || "",
          end: alloc.allocation_range?.end?.toString() || ""
        };

        if (alloc.reviewer_set === "SET_1") {
          set1.push(reviewer);
        } else if (alloc.reviewer_set === "SET_2") {
          set2.push(reviewer);
        }
      });

      setHumanAllocations((prev) => ({
        ...prev,
        phase2: { set1, set2 }
      }));
    }

    // Set Phase 1 AI Configs - transform API format to component format
    {
      const fallbackModel = "claude-sonnet-4-6";
      const modelKeys = new Set((availableModels || []).map((m) => m.model_key));
      const agents = (data.phase1_ai_configs || []).map((cfg) => ({
        id: cfg.id,
        // Saved agents keep the model they ran with even if it is no longer
        // selectable — see AiModelConfiguration's resolveModel.
        model: modelKeys.has(cfg.model_identifier) ? cfg.model_identifier : fallbackModel,
        temperature: cfg.config_json?.temperature ?? 0.2,
        saved: true,
      }));

      // Always keep at least 1 default agent so the UI never shows empty
      const finalAgents = agents.length > 0
        ? agents
        : [{ id: 1, model: fallbackModel, temperature: 0.2 }];

      setAiConfig((prev) => ({
        ...prev,
        phase1: {
          agents: finalAgents,
          default_agent_id: finalAgents[0]?.id ?? 1
        }
      }));
    }

    // Set Phase 2 AI Configs - transform API format to component format
    {
      const fallbackModel = "claude-sonnet-4-6";
      const modelKeys = new Set((availableModels || []).map((m) => m.model_key));
      const agents = (data.phase2_ai_configs || []).map((cfg) => ({
        id: cfg.id,
        // Saved agents keep the model they ran with even if it is no longer
        // selectable — see AiModelConfiguration's resolveModel.
        model: modelKeys.has(cfg.model_identifier) ? cfg.model_identifier : fallbackModel,
        temperature: cfg.config_json?.temperature ?? 0.2,
        saved: true,
      }));

      // Always keep at least 1 default agent so the UI never shows empty
      const finalAgents = agents.length > 0
        ? agents
        : [{ id: 1, model: fallbackModel, temperature: 0.2 }];

      setAiConfig((prev) => ({
        ...prev,
        phase2: {
          agents: finalAgents,
          default_agent_id: finalAgents[0]?.id ?? 1
        }
      }));
    }
  };


  // ====== Build payload (same structure used on Save) ======
  const buildFrameworkData = () => {
    const frameworkEnumMap = {
      human_only: "SINGLE_HUMAN_ONLY",
      ai_only: "SINGLE_AI_ONLY",
      human_ai: "DUAL_HUMAN_AI",
      human_human: "DUAL_HUMAN_HUMAN",
    };

    const framework = frameworkEnumMap[frameworkType] || null;

    const buildHumanAllocations = (phase, setKey, reviewerSetEnum) => {
      const list = humanAllocations?.[phase]?.[setKey] || [];
      const out = [];
      list.forEach((r) => {
        if (r.start && r.end) {
          out.push({
            reviewer_id: r.user_id,
            reviewer_set: reviewerSetEnum,
            allocation_range: {
              start: parseInt(r.start, 10),
              end: parseInt(r.end, 10),
            },
          });
        }
      });
      return out;
    };

    const phase1_human_allocations = [
      ...buildHumanAllocations("phase1", "set1", "SET_1"),
      ...buildHumanAllocations("phase1", "set2", "SET_2"),
    ];

    const phase2_human_allocations = [
      ...buildHumanAllocations("phase2", "set1", "SET_1"),
      ...buildHumanAllocations("phase2", "set2", "SET_2"),
    ];

    const DEFAULT_MODEL_ID = pickDefaultModel(availableModels);
    const DEFAULT_TEMPERATURE = 0.2;

    const transformAiConfig = (phaseConfig) => {
      if (phaseConfig?.agents && Array.isArray(phaseConfig.agents) && phaseConfig.agents.length > 0) {
        return phaseConfig.agents.map((agent) => ({
          model_identifier: agent.model || DEFAULT_MODEL_ID,
          config_json: {
            temperature: agent.temperature ?? DEFAULT_TEMPERATURE,
          },
        }));
      }

      if (Array.isArray(phaseConfig) && phaseConfig.length > 0) {
        return phaseConfig.map((cfg) => ({
          model_identifier: cfg.model_identifier || cfg.model || DEFAULT_MODEL_ID,
          config_json: cfg.config_json || { temperature: cfg.temperature ?? DEFAULT_TEMPERATURE },
        }));
      }

      return [];
    };

    const needsAiConfig = frameworkType === "ai_only" || frameworkType === "human_ai";

    let phase1_ai_configs = transformAiConfig(aiConfig?.phase1);
    let phase2_ai_configs = transformAiConfig(aiConfig?.phase2);

    if (needsAiConfig && phase1_ai_configs.length === 0) {
      phase1_ai_configs = [
        { model_identifier: DEFAULT_MODEL_ID, config_json: { temperature: DEFAULT_TEMPERATURE } },
      ];
    }
    if (needsAiConfig && phase2_ai_configs.length === 0) {
      phase2_ai_configs = [
        { model_identifier: DEFAULT_MODEL_ID, config_json: { temperature: DEFAULT_TEMPERATURE } },
      ];
    }

    return {
      framework,
      phase1_ai_assist_enabled: aiAssistantAbstract,
      phase2_ai_assist_enabled: aiAssistantFulltext,
      // Omitted when locked so the backend leaves Phase 1 agents untouched.
      ...(isAbstractLocked ? {} : { phase1_ai_configs }),
      phase2_ai_configs,
      phase1_human_allocations,
      phase2_human_allocations,
    };
  };

  // ✅ Emit form data upward for "unsaved changes" tracking
  useEffect(() => {
    if (typeof onDataChange === "function") {
      onDataChange(buildFrameworkData());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameworkType, frameworkGroup, humanAllocations, aiConfig, allocationType, selectedSet, humanAiTab, aiAssistantAbstract, aiAssistantFulltext]);

  /* ===================== EXPOSE getFrameworkData TO PARENT ===================== */
  useImperativeHandle(ref, () => ({
    getFrameworkData: () => buildFrameworkData(),
    hasErrors: () => hasGlobalErrors,
  }));

  const errors = useMemo(() => {
    const errs = {};
    const N = currentTotal || 0;

    reviewers.forEach((r) => {
      const s = Number(r.start);
      const e = Number(r.end);

      if (r.start === "" && r.end === "") {
        errs[r.rowId] = "Allocation range required";
        return;
      }

      if (N === 0 && (r.start !== "" || r.end !== "")) {
        errs[r.rowId] = "No papers available for allocation";
        return;
      }

      if (!s || !e) errs[r.rowId] = "Start and End required";
      else if (s < 1 || e < 1) errs[r.rowId] = "Positions are 1-based";
      else if (s > e) errs[r.rowId] = "Start must be ≤ End";
      else if (N && e > N) errs[r.rowId] = `End cannot exceed ${N}`;
    });

    // Overlap detection — compare all rows by rowId (same user multiple ranges counts too)
    reviewers.forEach((r) => {
      if (errs[r.rowId]) return;

      const s1 = Number(r.start);
      const e1 = Number(r.end);

      if (!s1 || !e1 || s1 > e1) return;

      for (const other of reviewers) {
        if (other.rowId === r.rowId) continue;

        const s2 = Number(other.start);
        const e2 = Number(other.end);

        if (!s2 || !e2 || s2 > e2) continue;

        if (s1 <= e2 && s2 <= e1) {
          const shortEmail =
            other.email.length > 10 ? `${other.email.slice(0, 10)}...` : other.email;
          errs[r.rowId] = `Overlaps with ${shortEmail}`;
          break;
        }
      }
    });

    return errs;
  }, [reviewers, currentTotal]);


  const hasErrors = Object.keys(errors).length > 0;

  // ---- Validation (all phases & sets - for save button) ----
  const hasGlobalErrors = useMemo(() => {
    const phases = ["phase1", "phase2"];
    const sets = ["set1", "set2"];

    for (const phase of phases) {
      const N = phase === "phase1" ? totals.abstract : totals.fulltext;

      for (const set of sets) {
        const setReviewers = humanAllocations[phase][set] || [];

        for (const r of setReviewers) {
          const s = Number(r.start);
          const e = Number(r.end);

          // Empty start & end
          if (r.start === "" && r.end === "") return true;

          // If no papers available but user entered values
          if (N === 0 && (r.start !== "" || r.end !== "")) return true;

          if (!s || !e) return true;
          if (s < 1 || e < 1) return true;
          if (s > e) return true;
          if (N && e > N) return true;

          // Overlap detection within same set (rowId used — same user's multiple rows also checked)
          for (const other of setReviewers) {
            if (other.rowId === r.rowId) continue;

            const s2 = Number(other.start);
            const e2 = Number(other.end);

            if (!s2 || !e2 || s2 > e2) continue;

            if (s <= e2 && s2 <= e) return true;
          }
        }
      }
    }

    return false;
  }, [humanAllocations, totals]);

  // ---- Strict no-overlap per active set ----
  const rangeError = useMemo(() => {
    const intervals = [];
    for (const r of reviewers) {
      const s = Number(r.start);
      const e = Number(r.end);
      if (!s || !e || s > e) continue;
      intervals.push([s, e]);
    }

    intervals.sort((a, b) => a[0] - b[0]);
    for (let i = 1; i < intervals.length; i++) {
      const [s] = intervals[i];
      const prevEnd = intervals[i - 1][1];
      if (s <= prevEnd) return "Overlapping allocation ranges are not allowed";
    }
    return "";
  }, [reviewers]);

  const totalPages = Math.ceil(teamTotal / pageSize) || 1;

  // papers assigned badge per active set
  const assignedUnique = useMemo(() => {
    const overall = currentTotal || 0;
    if (!overall) return 0;

    const intervals = [];
    for (const r of reviewers) {
      const s = parseInt(r.start, 10);
      const e = parseInt(r.end, 10);
      if (!Number.isFinite(s) || !Number.isFinite(e)) continue;
      if (s > e) continue;

      let ss = Math.max(1, s);
      let ee = Math.min(overall, e);
      if (ee < 1 || ss > overall) continue;

      intervals.push([ss, ee]);
    }

    if (!intervals.length) return 0;

    intervals.sort((a, b) => a[0] - b[0]);
    const merged = [];
    let [cs, ce] = intervals[0];

    for (let i = 1; i < intervals.length; i++) {
      const [s, e] = intervals[i];
      if (s <= ce + 1) ce = Math.max(ce, e);
      else {
        merged.push([cs, ce]);
        [cs, ce] = [s, e];
      }
    }
    merged.push([cs, ce]);

    return merged.reduce((sum, [s, e]) => sum + (e - s + 1), 0);
  }, [reviewers, currentTotal]);

  const assignedDisplay = `${assignedUnique}/${currentTotal || 0} Papers Assigned`;

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleSearchInputChange = (e) => setSearchInput(e.target.value);

  const setBucketReviewers = (nextList) => {
    setHumanAllocations((prev) => ({
      ...prev,
      [phaseKey]: {
        ...prev[phaseKey],
        [activeSetKey]: nextList,
      },
    }));
  };

  const addReviewer = (member) => {
    const uid = member.user_id ?? member.uid ?? member.id;

    if (isHumanHuman && excludeUserIds.includes(uid)) return;
    // Same user can be added multiple times with different ranges — no duplicate guard

    const rowId = `${uid}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setBucketReviewers([...reviewers, { rowId, user_id: uid, email: member.email, start: "", end: "" }]);
  };

  const removeReviewer = (rowId) => {
    setBucketReviewers(reviewers.filter((r) => r.rowId !== rowId));
  };

  const toggleReviewer = (member) => {
    // Always add — same user may appear multiple times with different ranges
    addReviewer(member);
  };

  const updateRange = (rowId, field, value) => {
    const v = value.replace(/\D/g, "");
    setBucketReviewers(reviewers.map((r) => (r.rowId === rowId ? { ...r, [field]: v } : r)));
  };

  const clearAll = () => setBucketReviewers([]);

  const autoAssign = () => {
    const N = currentTotal || 0;
    const M = reviewers.length;
    if (!M || !N) return;

    let start = 1;
    const next = reviewers.map((r, idx) => {
      const remain = N - start + 1;
      const slots = M - idx;
      const size = Math.floor(remain / slots);
      const end = idx === M - 1 ? N : start + size - 1;
      const row = { ...r, start: String(start), end: String(end) };
      start = end + 1;
      return row;
    });

    setBucketReviewers(next);
  };

  const updateAiConfig = (partial) => {
    setAiConfig((prev) => ({
      ...prev,
      [phaseKey]: partial,
    }));
  };

  const shouldShowAllocationArea = hasFrameworkSelected;

  const shouldShowHumanAllocation =
    hasFrameworkSelected &&
    (isHumanOnly || isHumanHuman || (isHumanAi && humanAiTab === "human"));

  const shouldShowAiConfig =
    hasFrameworkSelected &&
    (isAiOnly || (isHumanAi && humanAiTab === "ai"));

  return (
    <div className="slr-framework-form">
      <p className="ev-reg-p-dark-blue ev-bold-large mb-2">
        Select Framework <span className="text-danger">*</span>
      </p>

      {/* Framework cards */}
      <div className="row">
        <div className="col-lg-6 col-md-6 mb-3">
          <div className={`slr-Framework-card ev-radius-small p-2 h-100 ${frameworkGroup === "single" ? "active" : ""}`}>
            <p className={`ev-bold-med mb-2 px-1 ${frameworkGroup === "single" ? "ev-reg-p-dark-blue" : "ev-reg-p-dark"}`}>
              Single Review Screening
            </p>

            <div className="d-flex gap-2 flex-wrap">
              <button
                type="button"
                disabled={isEditMode && frameworkType !== "human_only"}
                className={`
                  ${frameworkGroup === "single" && frameworkType === "human_only"
                    ? "ev-btn-dark-blue-fill-round ev-btn-small ev-bold-med"
                    : "ev-btn-white-fill-round ev-btn-small ev-bold-med"}
                  ${isEditMode && frameworkType !== "human_only" ? "opacity-50" : ""}
                `}
                onClick={() => {
                  if (frameworkGroup === "single" && frameworkType === "human_only") return;
                  setFrameworkGroup("single");
                  setFrameworkType("human_only");
                  restoreFromStageData();
                }}
              >
                Human Only Screening
              </button>

              <button
                type="button"
                disabled={isEditMode && frameworkType !== "ai_only"}
                className={`
                  ${frameworkGroup === "single" && frameworkType === "ai_only"
                    ? "ev-btn-dark-blue-fill-round ev-btn-small ev-bold-med"
                    : "ev-btn-white-fill-round ev-btn-small ev-bold-med"}
                  ${isEditMode && frameworkType !== "ai_only" ? "opacity-50" : ""}
                `}
                onClick={() => {
                  if (frameworkGroup === "single" && frameworkType === "ai_only") return;
                  setFrameworkGroup("single");
                  setFrameworkType("ai_only");
                  restoreFromStageData();
                }}
              >
                AI Only Screening
              </button>
            </div>
          </div>
        </div>

        <div className="col-lg-6 col-md-6 mb-3">
          <div className={`slr-Framework-card ev-radius-small p-2 h-100 ${frameworkGroup === "dual" ? "active" : ""}`}>
            <p className={`ev-bold-med mb-2 px-1 ${frameworkGroup === "dual" ? "ev-reg-p-dark-blue" : "ev-reg-p-dark"}`}>
              Dual Review Screening
            </p>

            <div className="d-flex gap-2 flex-wrap">
              <button
                type="button"
                disabled={isEditMode && frameworkType !== "human_ai"}
                className={`
                  ${frameworkGroup === "dual" && frameworkType === "human_ai"
                    ? "ev-btn-dark-blue-fill-round ev-btn-small ev-bold-med"
                    : "ev-btn-white-fill-round ev-btn-small ev-bold-med"}
                  ${isEditMode && frameworkType !== "human_ai" ? "opacity-50" : ""}
                `}
                onClick={() => {
                  if (frameworkGroup === "dual" && frameworkType === "human_ai") return;
                  setFrameworkGroup("dual");
                  setFrameworkType("human_ai");
                  restoreFromStageData();
                }}
              >
                Human-AI Screening
              </button>

              <button
                type="button"
                disabled={isEditMode && frameworkType !== "human_human"}
                className={`
                  ${frameworkGroup === "dual" && frameworkType === "human_human"
                    ? "ev-btn-dark-blue-fill-round ev-btn-small ev-bold-med"
                    : "ev-btn-white-fill-round ev-btn-small ev-bold-med"}
                  ${isEditMode && frameworkType !== "human_human" ? "opacity-50" : ""}
                `}
                onClick={() => {
                  if (frameworkGroup === "dual" && frameworkType === "human_human") return;
                  setFrameworkGroup("dual");
                  setFrameworkType("human_human");
                  restoreFromStageData();
                }}
              >
                Human-Human Screening
              </button>
            </div>
          </div>
        </div>
      </div>

      {showAiAssistantToggle && (
        <div className="d-flex align-items-center gap-4 mb-3 flex-wrap">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-stars gradient-font6" />
            <span className="ev-reg-p-dark ev-bold-med">AI Assistant</span>
            <i
              className="bi bi-info-circle ev-small-p-blue"
              title="When on, the AI suggests a decision and reason for the reviewer to confirm or override. The reviewer always makes the final call."
            />
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="ev-small-p-dark opacity-75">Abstract Screening</span>
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input custom-switch"
                type="checkbox"
                id="aiAssistantAbstractToggle"
                checked={aiAssistantAbstract}
                onChange={(e) => setAiAssistantAbstract(e.target.checked)}
              />
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="ev-small-p-dark opacity-75">Full-Text Screening</span>
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input custom-switch"
                type="checkbox"
                id="aiAssistantFulltextToggle"
                checked={aiAssistantFulltext}
                onChange={(e) => setAiAssistantFulltext(e.target.checked)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Allocation area gated by selection */}
      {shouldShowAllocationArea ? (
        <>
          {/* === Inline allocation row for Human-AI/Human-Human === */}
          {(isHumanAi || isHumanHuman) ? (
            <div className="ev-card-2 ev-radius-small w-100 d-flex align-items-center gap-4 p-2 mb-2 flex-wrap">
              {/* Abstract Screening Allocation */}
              <div className="d-flex align-items-center gap-2 flex-wrap ev-card-3 p-1 ev-radius-small px-3 pe-2 ">
                <span className="ev-reg-p-dark ev-bold-med">Abstract Screening Allocation</span>
                {isHumanAi ? (
                  <>
                    <button
                      type="button"
                      className={
                        allocationType === "abstract" && humanAiTab === "human"
                          ? "ev-btn-blue-fill-round ev-btn-reg ev-bold-med ev-radius-xsmall"
                          : "ev-btn-light-fill ev-btn-reg ev-bold-med ev-radius-xsmall"
                      }
                      onClick={() => {
                        setAllocationType("abstract");
                        setHumanAiTab("human");
                      }}
                    >
                      Human Configuration
                    </button>
                    <button
                      type="button"
                      className={
                        allocationType === "abstract" && humanAiTab === "ai"
                          ? "ev-btn-blue-fill-round ev-btn-reg ev-bold-med ev-radius-xsmall"
                          : "ev-btn-light-fill ev-btn-reg ev-bold-med ev-radius-xsmall"
                      }
                      onClick={() => {
                        setAllocationType("abstract");
                        setHumanAiTab("ai");
                      }}
                    >
                      AI Configuration
                    </button>
                  </>
                ) : null}
                {isHumanHuman ? (
                  <>
                    <button
                      type="button"
                      className={
                        allocationType === "abstract" && selectedSet === 1
                          ? "ev-btn-blue-fill-round ev-btn-reg ev-bold-med ev-radius-xsmall"
                          : "ev-btn-light-fill ev-btn-reg ev-bold-med ev-radius-xsmall"
                      }
                      onClick={() => {
                        setAllocationType("abstract");
                        setSelectedSet(1);
                      }}
                    >
                      Human Reviewer Set 1
                    </button>
                    <button
                      type="button"
                      className={
                        allocationType === "abstract" && selectedSet === 2
                          ? "ev-btn-blue-fill-round ev-btn-reg ev-bold-med ev-radius-xsmall"
                          : "ev-btn-light-fill ev-btn-reg ev-bold-med ev-radius-xsmall"
                      }
                      onClick={() => {
                        setAllocationType("abstract");
                        setSelectedSet(2);
                      }}
                    >
                      Human Reviewer Set 2
                    </button>
                  </>
                ) : null}
              </div>

              {/* Full-Text Screening Allocation */}
              <div className="d-flex align-items-center gap-2 flex-wrap ev-card-3 p-1 ev-radius-small px-3 pe-2">
                <span className="ev-reg-p-dark ev-bold-med">Full-Text Screening Allocation</span>
                {isHumanAi ? (
                  <>
                    <button
                      type="button"
                      className={
                        allocationType === "fulltext" && humanAiTab === "human"
                          ? "ev-btn-blue-fill-round ev-btn-reg ev-bold-med ev-radius-xsmall"
                          : "ev-btn-light-fill ev-btn-reg ev-bold-med ev-radius-xsmall"
                      }
                      onClick={() => {
                        setAllocationType("fulltext");
                        setHumanAiTab("human");
                      }}
                    >
                      Human Configuration
                    </button>
                    <button
                      type="button"
                      className={
                        allocationType === "fulltext" && humanAiTab === "ai"
                          ? "ev-btn-blue-fill-round ev-btn-reg ev-bold-med ev-radius-xsmall"
                          : "ev-btn-light-fill ev-btn-reg ev-bold-med ev-radius-xsmall"
                      }
                      onClick={() => {
                        setAllocationType("fulltext");
                        setHumanAiTab("ai");
                      }}
                    >
                      AI Configuration
                    </button>
                  </>
                ) : null}
                {isHumanHuman ? (
                  <>
                    <button
                      type="button"
                      className={
                        allocationType === "fulltext" && selectedSet === 1
                          ? "ev-btn-blue-fill-round ev-btn-reg ev-bold-med ev-radius-xsmall"
                          : "ev-btn-light-fill ev-btn-reg ev-bold-med ev-radius-xsmall"
                      }
                      onClick={() => {
                        setAllocationType("fulltext");
                        setSelectedSet(1);
                      }}
                    >
                      Human Reviewer Set 1
                    </button>
                    <button
                      type="button"
                      className={
                        allocationType === "fulltext" && selectedSet === 2
                          ? "ev-btn-blue-fill-round ev-btn-reg ev-bold-med ev-radius-xsmall"
                          : "ev-btn-light-fill ev-btn-reg ev-bold-med ev-radius-xsmall"
                      }
                      onClick={() => {
                        setAllocationType("fulltext");
                        setSelectedSet(2);
                      }}
                    >
                      Human Reviewer Set 2
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          ) : (
            /* Human Only/AI only: keep simple phase tabs */
            <div className="ev-card-2 ev-radius-small w-100 d-flex gap-2 p-2 mb-3">
              <button
                type="button"
                className={
                  allocationType === "abstract"
                    ? "ev-btn-blue-fill-round ev-btn-reg ev-bold-med ev-radius-xsmall"
                    : "ev-btn-white-fill-round ev-btn-reg ev-bold-med ev-radius-xsmall"
                }
                onClick={() => setAllocationType("abstract")}
              >
                Abstract Screening Allocation
              </button>

              <button
                type="button"
                className={
                  allocationType === "fulltext"
                    ? "ev-btn-blue-fill-round ev-btn-reg ev-bold-med ev-radius-xsmall"
                    : "ev-btn-white-fill-round ev-btn-reg ev-bold-med ev-radius-xsmall"
                }
                onClick={() => setAllocationType("fulltext")}
              >
                Full-Text Screening Allocation
              </button>
            </div>
          )}

          {/* Human allocation */}
          {shouldShowHumanAllocation ? (
            <HumanReviewerAllocation
              team={team}
              teamTotal={teamTotal}
              reviewers={reviewers}
              totals={{ overall: currentTotal }}
              assignedDisplay={assignedDisplay}
              rangeError={rangeError}
              errors={errors}
              hasErrors={hasErrors}
              excludeUserIds={excludeUserIds}
              isLoadingTeam={isLoadingTeam}

              page={page}
              totalPages={totalPages}
              pageSize={pageSize}
              searchInput={searchInput}
              onSearchInputChange={handleSearchInputChange}
              onSearchKeyPress={handleSearchKeyPress}
              onSearch={handleSearch}
              onPrevPage={() => setPage((p) => Math.max(1, p - 1))}
              onNextPage={() => setPage((p) => Math.min(totalPages, p + 1))}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setPage(1);
              }}
              onToggleReviewer={toggleReviewer}
              onUpdateRange={updateRange}
              onRemoveReviewer={removeReviewer}
              onClearAll={clearAll}
              onAutoAssign={autoAssign}
              onRefreshCounts={handleRefreshCounts}
              isRefreshingCounts={isRefreshingCounts}
            />
          ) : null}

          {/* AI config */}
          {shouldShowAiConfig ? (
            <AiModelConfiguration
              phaseKey={phaseKey}
              value={aiConfig[phaseKey]}
              onChange={updateAiConfig}
              availableModels={availableModels}
              locked={phaseKey === "phase1" && isAbstractLocked}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
});

export default ScreeningFrameworkSelectionForm;